import { memo, useCallback, useMemo, useState } from 'react';
import { FaChevronRight, FaFile, FaFolder, FaFolderOpen } from 'react-icons/fa';

const BLOCK = 'LStudio__app_tree';

/*
    🔥 ÁRBOL / EXPLORADOR

    nodes: [{ id, label, icon, children, isLeaf, meta, isDisabled }]

    ⚠️ NO ES FieldTree

    FieldTree EDITA una jerarquía dentro de un formulario. Esto la
    NAVEGA: carpetas que se abren, selección, y una ruta que se puede
    seguir.

    ⚠️ EL TECLADO SIGUE EL PATRÓN DE ÁRBOL DE ARIA

      ↓ ↑      recorren lo VISIBLE (no los hijos plegados)
      →        abre la carpeta, o baja al primer hijo si ya está abierta
      ←        cierra la carpeta, o sube al padre si ya está cerrada
      Enter    selecciona

    Aplanar solo lo visible es la clave: si ↓ recorriera todo el árbol,
    el foco desaparecería adentro de carpetas cerradas.

    ⚠️ LA SANGRÍA VA POR CSS, NO POR PADDING INLINE

    El nivel viaja como custom property y el CSS multiplica. Así la
    densidad cambia con el nivel de estética como todo lo demás.
*/

// 🔥 Aplana solo lo que se está viendo: es lo que recorren las flechas
const onFlattenVisible = (nodes, expanded, level = 0, parentId = null, acc = []) => {

    nodes.forEach((node) => {

        acc.push({ ...node, level, parentId });

        const HAS_CHILDREN = !node.isLeaf && (node.children ?? []).length > 0;

        if (HAS_CHILDREN && expanded.includes(node.id)) {
            onFlattenVisible(node.children, expanded, level + 1, node.id, acc);
        }
    });

    return acc;
};

/*
    🔥 NODO MEMOIZADO

    ⚠️ Un explorador con 300 archivos re-renderizaba los 300 cada vez
       que se abría o cerraba una sola carpeta. Con memo, solo cambian
       los que aparecen o desaparecen.
*/
const TreeNode = memo(({
    node, position, hasChildren, isOpen, isSelected, isTabbable,
    variant, onToggleNode, onSelect, onKeyDown,
}) => (

    <div
        id={`${BLOCK}_${node.id}`}
        className={`${BLOCK}__node`}
        role="treeitem"
        aria-expanded={hasChildren ? isOpen : undefined}
        aria-selected={isSelected}
        aria-level={node.level + 1}
        /*
            ⚠️ Un solo nodo tabulable: el árbol entero es UNA parada de
               tabulación y adentro se navega con flechas. Si todos
               fueran tabIndex 0, salir de un árbol de 200 nodos serían
               200 tabs.
        */
        tabIndex={isTabbable ? 0 : -1}
        data-selected={isSelected}
        data-disabled={!!node.isDisabled}
        style={{ '--LS__tree_level': node.level }}
        onClick={() => {
            if (node.isDisabled) return;
            if (hasChildren) onToggleNode(node.id);
            onSelect?.(node);
        }}
        onKeyDown={(e) => onKeyDown(e, node, position)}
    >

        <span className={`${BLOCK}__caret`} data-open={isOpen}
            data-hidden={!hasChildren} aria-hidden="true" >
            <FaChevronRight />
        </span>

        <span className={`${BLOCK}__icon`} aria-hidden="true">
            {
                node.icon
                    ?? (variant === 'plain' ? null
                        : hasChildren
                            ? (isOpen ? <FaFolderOpen /> : <FaFolder />)
                            : <FaFile />)
            }
        </span>

        <span className={`${BLOCK}__label`}>{node.label}</span>

        {node.meta && <span className={`${BLOCK}__meta`}>{node.meta}</span>}

    </div>
));

TreeNode.displayName = 'TreeNode';

export const AppTree = ({
    nodes = [],
    selectedId = null,
    onSelect = null,
    defaultExpanded = [],
    expandedIds = null,          // controlado desde afuera si lo pasás
    onToggle = null,
    variant = 'files',           // files | plain
    showLines = true,
    emptyLabel = 'Sin elementos',
}) => {

    const [useInner, setInner] = useState(defaultExpanded);

    const EXPANDED = expandedIds ?? useInner;

    const MEMO__VISIBLE = useMemo(
        () => onFlattenVisible(nodes, EXPANDED),
        [nodes, EXPANDED],
    );

    const CB__onToggleNode = useCallback((id) => {

        const NEXT = EXPANDED.includes(id)
            ? EXPANDED.filter((x) => x !== id)
            : [...EXPANDED, id];

        if (expandedIds === null) setInner(NEXT);

        onToggle?.(id, NEXT);

    }, [EXPANDED, expandedIds, onToggle]);

    const onKeyDown = (event, node, position) => {

        const HAS_CHILDREN = !node.isLeaf && (node.children ?? []).length > 0;
        const IS_OPEN = EXPANDED.includes(node.id);

        const onFocusAt = (index) => {
            const TARGET = MEMO__VISIBLE[index];
            if (!TARGET) return;
            document.getElementById(`${BLOCK}_${TARGET.id}`)?.focus();
        };

        switch (event.key) {

            case 'ArrowDown':
                event.preventDefault();
                onFocusAt(position + 1);
                break;

            case 'ArrowUp':
                event.preventDefault();
                onFocusAt(position - 1);
                break;

            case 'ArrowRight':
                event.preventDefault();
                if (HAS_CHILDREN && !IS_OPEN) CB__onToggleNode(node.id);
                else if (HAS_CHILDREN) onFocusAt(position + 1);
                break;

            case 'ArrowLeft':
                event.preventDefault();
                if (HAS_CHILDREN && IS_OPEN) CB__onToggleNode(node.id);
                else if (node.parentId) document.getElementById(`${BLOCK}_${node.parentId}`)?.focus();
                break;

            case 'Enter':
            case ' ':
                event.preventDefault();
                if (HAS_CHILDREN) CB__onToggleNode(node.id);
                onSelect?.(node);
                break;

            default:
                break;
        }
    };

    if (!nodes.length) return <p className={`${BLOCK}__empty`}>{emptyLabel}</p>;

    return (
        <div className={BLOCK} role="tree" data-variant={variant} data-lines={showLines}>
            {
                MEMO__VISIBLE.map((node, position) => {

                    const HAS_CHILDREN = !node.isLeaf && (node.children ?? []).length > 0;
                    const IS_OPEN = EXPANDED.includes(node.id);
                    const IS_SELECTED = node.id === selectedId;

                    return (
                        <TreeNode
                            key={node.id}
                            node={node}
                            position={position}
                            hasChildren={HAS_CHILDREN}
                            isOpen={IS_OPEN}
                            isSelected={IS_SELECTED}
                            isTabbable={IS_SELECTED || (!selectedId && position === 0)}
                            variant={variant}
                            onToggleNode={CB__onToggleNode}
                            onSelect={onSelect}
                            onKeyDown={onKeyDown}
                        />
                    );
                })
            }
        </div>
    );
};
